"use client"

import { useRef, useState, Suspense } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import { SearchParamsHydrationGate } from "@/components/ssr/search-params-hydration-gate"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { CostForm } from "@/components/tools/cost-form"
import { CostResults } from "@/components/tools/cost-results"
import { type LawnCostResult } from "@/lib/cost-calculator"
import { HeadlineHero, LawnPrefillBanner, RelatedTools, ResultEmailCaptureCard, ResultSuccessCard, ToolPageLoadingFallback } from "@/components/tools/shared"
import type { ResultSuccessCardStat } from "@/components/tools/shared"
import { CalendarDays, Hammer, Briefcase } from "lucide-react"
import { buildRelatedToolUrl, buildToolUrl } from "@/lib/result-tool-urls"
import { toolNavigationAnalytics, analytics, stashEmailCaptureAttribution } from "@/lib/analytics"

import { useAuth } from "@/contexts/AuthContext"
import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"
import { NewsletterSignupModal } from "@/components/ui/newsletter-signup-modal"
import { ProductGuideCta } from "@/components/lead-magnets/product-guide-cta"
import { ProductRecommendations } from "@/components/tools/product-recommendations"
import { motion } from "motion/react"
import { FADE_UP, FADE_TRANSITION } from "@/lib/motion-presets"
import { Reveal } from "@/components/motion"
import { useFromTool } from "@/hooks/use-from-tool"
import { useDefaultLawn } from "@/hooks/use-default-lawn"

function LawnCostEstimatorContent() {
  const searchParams = usePrerenderSafeSearchParams()
  useFromTool("lawn-cost-estimator")
  const { lawns, selectedLawnId, setSelectedLawnId, prefill: lawnPrefill } = useDefaultLawn()
  const { subscription } = useAuth()
  const { trackUsage, showNewsletterModal, closeNewsletterModal, usageCount } = useNewsletterPrompt()
  const [result, setResult] = useState<LawnCostResult | null>(null)
  const trackedUsageRef = useRef(false)

  // The form computes the estimate live (once the ZIP resolves to a known
  // region) and streams it up. Usage analytics fire exactly once per session
  // via the ref guard, preserving the same events the old submit path fired.
  const handleResultChange = (
    calculationResult: LawnCostResult | null,
    inputs?: { includeEquipment: boolean; includeWater: boolean },
  ) => {
    setResult(calculationResult)
    if (calculationResult && !trackedUsageRef.current) {
      trackedUsageRef.current = true
      trackUsage('cost-estimator')
      analytics.calculatorUsed('cost', {
        inputs: {
          grass_type: calculationResult.grassType,
          zip_code: calculationResult.zipCode,
          lawn_size_sqft: calculationResult.lawnSize,
          service_level: calculationResult.serviceLevel,
          care_approach: calculationResult.careApproach,
          include_equipment: !!inputs?.includeEquipment,
          include_water: !!inputs?.includeWater,
        },
        result: {
          region: calculationResult.region,
          monthly_total: calculationResult.monthlyTotal,
          annual_total: calculationResult.annualTotal,
          cost_per_1000_sqft: calculationResult.costPer1000SqFt,
          diy_total: calculationResult.diyTotal,
          professional_total: calculationResult.professionalTotal,
          savings_vs_professional: calculationResult.savingsVsProfessional,
        },
        userTier: subscription?.isPremium ? 'premium' : 'free',
        location: calculationResult.zipCode,
        source: 'live_update',
      })
    }
  }

  return (
    <div className="min-h-screen bg-linear-to-br from-green-50 via-white to-gray-50">
      <Reveal onMount y={6} className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8 pt-4 md:pt-8 pb-8 md:pb-16">
        {/* Breadcrumbs */}
        <BreadcrumbComponent
          items={[
            { label: "Home", href: "/" },
            { label: "Tools", href: "/tools" },
            { label: "Lawn Cost Estimator" }
          ]}
        />

        <div className="max-w-5xl mx-auto">
        {/* Hero matches sister tools (fertilizer-calculator etc.) */}
        <div className="mb-8 md:mb-12">
          {/* The visible H1 must carry the exact head term. The SSR shell
              already renders "Free Lawn Mowing Cost Calculator" via the
              Suspense fallback below, but the hydrated hero is the H1 a
              rendering crawler and a human actually see, and it used to say
              nothing about mowing. Keep the two in the same language. */}
          <HeadlineHero
            eyebrow="Personalized to your ZIP"
            title="Free lawn mowing cost calculator"
            accent="for your yard and ZIP"
            subtitle="Mowing cost per visit by lawn size, per-acre rates for bigger properties, and what a full year of lawn care runs. DIY vs professional, updating as you go."
          />
        </div>

        {/* Form */}
        <div className="mb-8">
          <LawnPrefillBanner lawns={lawns} selectedLawnId={selectedLawnId} onSelect={setSelectedLawnId} />
          <CostForm
            initialValues={{
              zipCode: searchParams.get('zip') ?? lawnPrefill.zipCode,
              grassType: searchParams.get('grass') ?? lawnPrefill.grassType,
              sunlight: searchParams.get('sunlight') ?? lawnPrefill.sunlight,
              lawnSize: searchParams.get('size') ? undefined : lawnPrefill.lawnSizeSqft,
            }}
            onResultChange={handleResultChange}
          />
        </div>

        {/* Results */}
        {result && (
          <div id="results" className="space-y-6">
            <CostSuccessCard result={result} />
            <ResultEmailCaptureCard
              source="tool_result_cost"
              toolDisplayName="cost estimate"
              subcopy="Seasonal tips to keep your lawn costs down."
              grassType={result.grassType}
              zipCode={result.zipCode}
              accountLead={{
                redirectTo: buildToolUrl("lawn-cost-estimator", {
                  grassType: result.grassType,
                  zip: result.zipCode,
                }),
                headline: `Save my cost estimate for ${result.grassType}`,
                subcopy: "Keep this cost estimate in a free account and track your lawn over time.",
                dividerLabel: "or get your 12-month calendar by email",
                onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_cost"),
              }}
              onCaptured={() => {
                closeNewsletterModal()
              }}
            />
            <CostResults result={result} />
            <ProductGuideCta source="lawn_cost_estimator_result" variant="result" />
          </div>
        )}

        <RelatedTools
          currentToolId="lawn-cost-estimator"
          prefillValues={{
            zip: searchParams.get('zip') ?? lawnPrefill.zipCode,
            grassType: result?.grassType ?? lawnPrefill.grassType,
            lawnSize: result?.lawnSize ?? lawnPrefill.lawnSizeSqft,
            sunlight: searchParams.get('sunlight') ?? lawnPrefill.sunlight,
            serviceLevel: result?.serviceLevel,
            careApproach: result?.careApproach,
          }}
        />

        {/* Pre-submission content */}
        {!result && (<>
          <div className="mt-8">
            <ProductRecommendations
              categories={["Fertilizers & Soil Health", "Lawn Mowers & Maintenance Tools"]}
              title="Cost-Effective Lawn Products"
              description="Popular products to get the most value from your lawn care budget"
              trackingContext="lawn-cost-estimator-pre-submission"
              compact
            />
          </div>
        </>)}
        </div>
      </Reveal>

      {/* Newsletter signup modal - shows after 1st calculator use with 3.5s delay */}
      <NewsletterSignupModal
        isOpen={showNewsletterModal}
        onClose={closeNewsletterModal}
        toolName="Lawn Cost Estimator"
        usageCount={usageCount}
        grassType={result?.grassType}
        zipCode={result?.zipCode}
      />
    </div>
  )
}

const APPROACH_LABELS: Record<LawnCostResult["careApproach"], string> = {
  diy: "DIY",
  professional: "professional",
  hybrid: "hybrid",
}

/**
 * Bounded peak-moment frame for the cost result. Hero = annual total cost
 * (the single most motivating number for the user). Next-action: fertilizer
 * calculator (the biggest recurring DIY cost after the estimate lands).
 */
function CostSuccessCard({ result }: { result: LawnCostResult }) {
  const annualTotal = result.annualTotal
  const approachLabel = APPROACH_LABELS[result.careApproach]
  const sizeLabel = result.lawnSize.toLocaleString()

  const fertilizerHref = buildRelatedToolUrl("fertilizer-calculator", "lawn-cost-estimator", {
    zip: result.zipCode,
    grassType: result.grassType,
    lawnSize: result.lawnSize,
  })

  const handleCtaClick = () => {
    toolNavigationAnalytics.relatedToolClicked({
      sourceTool: "lawn-cost-estimator",
      targetTool: "fertilizer-calculator",
      rank: 1,
      placement: "success_card",
      recommendationReason: "Fertilization is the largest controllable DIY cost",
      prefillFields: ["zip", "grass", "size"]
        .filter((f) => {
          if (f === "zip") return Boolean(result.zipCode)
          return true
        })
        .join(","),
    })
  }

  const usd0 = (n: number) => `$${Math.round(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`

  // Decision-useful secondary metrics the calculator already computed: the
  // effective monthly spend, and the DIY-vs-professional spread (the exact
  // trade-off the user is weighing). Guard the optional comparison totals.
  const stats: ResultSuccessCardStat[] = [
    { icon: CalendarDays, label: "Per month", value: usd0(result.monthlyTotal) },
  ]
  if (result.diyTotal) {
    stats.push({ icon: Hammer, label: "DIY / year", value: usd0(result.diyTotal) })
  }
  if (result.professionalTotal) {
    stats.push({ icon: Briefcase, label: "Pro / year", value: usd0(result.professionalTotal) })
  }

  return (
    <ResultSuccessCard
      heroValue={`$${annualTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
      heroCountTo={annualTotal}
      heroCountFormat={(n) => `$${Math.round(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
      heroLabel={`estimated annual ${approachLabel} cost`}
      stats={stats}
      interpretation={
        <>
          A {approachLabel} lawn care program for your {sizeLabel} sq ft {result.grassType} lawn in the {result.region} region runs about ${annualTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })} per year (${result.monthlyTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo).
        </>
      }
      cta={{
        href: fertilizerHref,
        label: "Calculate your fertilizer needs",
        onClick: handleCtaClick,
      }}
    />
  )
}

// Default export with Suspense boundary for useSearchParams
export function LawnCostEstimatorPageContent() {
  return (
    <Suspense fallback={<ToolPageLoadingFallback toolId="lawn-cost-estimator" headingOverride="Free Lawn Mowing Cost Calculator" />}>
      <SearchParamsHydrationGate>
        <LawnCostEstimatorContent />
      </SearchParamsHydrationGate>
    </Suspense>
  )
}
